import { Dialog, Typography } from '@mui/material'
import { RaRecord, useRefresh } from 'ra-core'
import { useState } from 'react'
import { Create, Datagrid, List, SimpleForm, TextField, useTranslate } from 'react-admin'
import styled from 'styled-components'

import { CustomDatagridBody } from 'components/common/CustomDatagridBody'
import RetailerAddressFormInputs from './RetailerAddressFormInputs'
import RetailerAddressListToolbar from './RetailerAddressListToolbar'

type RetailerAddressListProps = {
    filter?: RaRecord
}

const RetailerAddressList = (props: RetailerAddressListProps) => {
    const RESOURCE = 'RetailerAddress' as const

    // for some reason we need a small r here
    const resourceTranslation = 'retailerAddress'
    const [open, setOpen] = useState(false)
    const reload = useRefresh()
    const translate = useTranslate()
    const retailerId = props?.filter?.retailer_id

    // Toggle modal state
    const toggleModal = (): void => {
        setOpen(!open)
    }

    // Closes modal and re-renders after saving
    const handleSuccess = () => {
        toggleModal()
        reload()
    }

    return (
        <>
            <ListWrapper>
                <List
                    resource={RESOURCE}
                    key={`retailer-addresses-${retailerId}`}
                    actions={
                        <RetailerAddressListToolbar
                            resource={resourceTranslation}
                            handleCreateCallback={() => toggleModal()}
                        />
                    }
                    empty={false}
                    sx={{
                        width: '100%',
                        margin: '2rem 0'
                    }}
                    sort={{ field: 'address_label', order: 'ASC' }}
                    filter={props.filter}
                    disableSyncWithLocation={true}
                >
                    <Datagrid
                        body={<CustomDatagridBody bulkActionButtons={false} />}
                        rowClick="edit"
                        empty={<>{translate('manager.resources.retailerAddress.empty')}</>}
                    >
                        <TextField
                            source="address_label"
                            sortable={false}
                            label={translate('manager.resources.retailerAddress.address_label')}
                        />
                        <TextField
                            source="id"
                            sortable={false}
                            label={translate('manager.resources.retailerAddress.id')}
                        />
                        <TextField
                            source="street"
                            sortable={false}
                            label={translate('manager.resources.retailerAddress.street')}
                        />
                        <TextField
                            source="post_code"
                            sortable={false}
                            label={translate('manager.resources.retailerAddress.post_code')}
                        />
                        <TextField
                            source="city"
                            sortable={false}
                            label={translate('manager.resources.retailerAddress.city')}
                        />
                        <TextField
                            source="country_code"
                            sortable={false}
                            label={translate('manager.resources.retailerAddress.country_code')}
                        />
                    </Datagrid>
                </List>
            </ListWrapper>

            <Dialog open={open} onClose={() => toggleModal()}>
                <Create resource={RESOURCE} mutationOptions={{ onSuccess: handleSuccess }}>
                    <SimpleForm
                        sx={{
                            width: 500
                        }}
                        defaultValues={{ retailer_id: retailerId }}
                    >
                        <Typography variant="h6" gutterBottom>
                            {translate(`manager.resources.${resourceTranslation}.add`)}
                        </Typography>

                        <RetailerAddressFormInputs />
                    </SimpleForm>
                </Create>
            </Dialog>
        </>
    )
}

const ListWrapper = styled.section`
    width: 100%;
    & .MuiPaper-root {
        border: 0;
    }
`

export default RetailerAddressList
